import { mapConfig } from 'lib/config'
import { Position } from 'lib/place'
import { BaseLayerNames } from 'map/layers'

import { AppEvent, logAppEvent } from '../lib/analytics'

type MapSearchParamValues = Partial<MapSearchParamString>

type MapSearchParamString = {
  [key in keyof MapSearchParam]: string
}

export interface MapSearchParam {
  lat: number
  lng: number
  zoom: number
  baseLayer: string
}

export type WaypointTuple = [string, number, number]

enum MapSearchParamValueKey {
  lat = 'lat',
  lng = 'lng',
  zoom = 'zoom',
  baseLayer = 'baseLayer',
}

interface DecodedPlace {
  address?: string
  name?: string
  latitude?: number
  longitude?: number
  providerId?: number
  providerName?: string
}

// export it for testing reasons
export const defaultValue = Object.freeze({
  [MapSearchParamValueKey.baseLayer]: String(mapConfig.layers.vector.omv.name),
  [MapSearchParamValueKey.lat]: String(mapConfig.geolocation.defaultPosition.lat),
  [MapSearchParamValueKey.lng]: String(mapConfig.geolocation.defaultPosition.lng),
  [MapSearchParamValueKey.zoom]: String(mapConfig.geolocation.defaultZoomLevel),
}) as MapSearchParamString

/**
 * A helper function that updates or creates map search param composite value
 *
 * The search param map value is consisted of multiple values that are separated by commas.
 * @param newValues is the object includes the values need to updated in the map search param
 * @param currentParamValue is the current parameter value in format lat,lng,zoom,baseLayer
 * @param defaultMapSearchParam is the object of default map param values, derive from config module
 * @returns {(string|undefined)} The updated search param value or no value in case of invalid input
 */
export function createMapSearchParam(
  newValues: MapSearchParamValues,
  currentParamValue?: string,
  defaultMapSearchParam: MapSearchParamString = defaultValue
): string | undefined {
  if (areAllParamValuesSet(newValues)) {
    return orderMapSearchParamValues(newValues).join(',')
  }

  const currentValue = currentParamValue === undefined ? '' : currentParamValue
  const currentMapParam = parseMapSearchParam(currentValue, defaultMapSearchParam)
  if (currentMapParam === undefined) {
    return
  }

  //keep the new values intact if are valid and replace them with the default or current values if they are not.
  const validNewValues = (Object.keys(newValues) as MapSearchParamValueKey[]).reduce(
    (acc: MapSearchParamValues, key) => {
      const numbersTest = [
        MapSearchParamValueKey.lat,
        MapSearchParamValueKey.lng,
        MapSearchParamValueKey.zoom,
      ]
      if (numbersTest.includes(key)) {
        return {
          ...acc,
          [key]: isNumber(newValues[key] as string) ? newValues[key] : currentMapParam[key],
        }
      }
      const baseLayerNames: string[] = Object.values(BaseLayerNames)
      return {
        ...acc,
        baseLayer: baseLayerNames.includes(newValues[key] as string)
          ? newValues[key]
          : currentMapParam.baseLayer,
      }
    },
    {} as MapSearchParamValues
  )

  return orderMapSearchParamValues({
    ...mapMapSearchParamToMapSearchParamString(currentMapParam),
    ...validNewValues,
  }).join(',')
}

/**
 * Helper function that gets map search param and return an object of its included values
 *
 * @param searchParam The parameter value you want to destructure
 * @param defaultMapSearchParam is the object of default map param values, derive from config module
 * @returns MapSearchParam object
 */
export function parseMapSearchParam(
  searchParam: string,
  defaultMapSearchParam: MapSearchParamString = defaultValue
): MapSearchParam {
  const [latStr, lngStr, zoomStr, baseLayerName] = searchParam.split(',')

  const lat = isNumber(latStr) ? Number(latStr) : Number(defaultMapSearchParam.lat)
  const lng = isNumber(lngStr) ? Number(lngStr) : Number(defaultMapSearchParam.lng)
  const zoom = isNumber(zoomStr) ? Number(zoomStr) : Number(defaultMapSearchParam.zoom)

  const baseLayerNames: string[] = Object.values(BaseLayerNames)
  const baseLayer = baseLayerNames.includes(baseLayerName)
    ? baseLayerName
    : defaultMapSearchParam.baseLayer

  return {
    baseLayer,
    lat,
    lng,
    zoom,
  }
}

/**
 * Helper function that checks if the string number is not NaN, and if the final number value doesn't changed during auto coercion
 *
 * @param value the string value represents a number
 * @returns a boolean value indicated whether the string input is a number or not
 */
function isNumber(value: string): boolean {
  const numberValue = Number(value)
  if (!isNaN(numberValue)) {
    if (numberValue === 0 && value !== '0' && value !== '-0') {
      return false
    }
    return true
  }

  return false
}

/**
 * Use this predicate to ensure that partial representation of the MapSearchParamString entity
 * must include all the available properties
 *
 * @param value MapSearchParamString partial value
 * @returns the MapSearchParamString type predicate
 */
function areAllParamValuesSet(value: MapSearchParamValues): value is MapSearchParamString {
  const newKeysAvailable = JSON.stringify(Object.keys(value).sort())
  const allAvailableKeys = JSON.stringify(Object.keys(MapSearchParamValueKey).sort())

  return newKeysAvailable === allAvailableKeys
}

/**
 * Place MapSearchParamValues into the right order.
 * lat has the first place
 * lng the second
 * ...
 * and so on
 *
 * @param param  MapSearchParamString instance includes all the params
 * @returns Array<string> the ordered array of values
 */
function orderMapSearchParamValues({
  lat,
  lng,
  zoom,
  baseLayer,
}: MapSearchParamString): Array<string> {
  return [lat, lng, zoom, baseLayer]
}

/**
 * Turns the search param object into an object with string params
 *
 * @param param MapSearchParam the search param object
 * @returns MapSearchParamString the string representation of MapSearchParam instance
 */
function mapMapSearchParamToMapSearchParamString(param: MapSearchParam): MapSearchParamString {
  return {
    baseLayer: param.baseLayer,
    lat: String(param.lat),
    lng: String(param.lng),
    zoom: String(param.zoom),
  }
}

/**
 * A function that gets the route waypoints tuples and returns a string path
 *
 * @param waypoints An array of tuples include the waypoints title lat and lng
 * @returns the waypoints in a comma, slash formatted string encoded in a URI or empty string in case of invalid input data
 */
export function createWaypointsPath(waypoints: (WaypointTuple | undefined)[]): string {
  return waypoints.reduce((acc, waypoint, index) => {
    let link = ''

    if (waypoint) {
      if (waypoint[0]) link = `${encodeURIComponent(waypoint[0])},`
      if (waypoint[1]) link = `${link}${waypoint[1]},`
      if (waypoint[2]) link = `${link}${waypoint[2]}`
    }

    return link ? acc + `${link}` + (index === waypoints.length - 1 ? '' : '/') : acc + '/'
  }, '')
}

/**
 * A private function that gets the route path and returns the waypoint tuples
 *
 * The source and destination paths are separated by "/", and the tuple value are separated by ","
 *
 * @param path the waypoints in a comma formatted string
 * @param currentUserPosition
 * @returns the array of waypoint tuples, the array might include undefined values that can be used as placeholder for the index.
 */
export function getPathWaypoints(
  path: string,
  currentUserPosition?: Position | null
): (WaypointTuple | undefined | string)[] {
  if (path !== '') {
    const rawPaths = path.trim().replace(/\/$/, '').split('/')

    return rawPaths.map((path: string): WaypointTuple | undefined | string =>
      getWaypointTuple(path, currentUserPosition)
    )
  }

  return []
}

/**
 * A private helper function that gets the partial path and return the waypoint tuple
 *
 * @param partial the path that represents a specific waypoint
 * @param currentUserPosition
 * @returns WaypointTuple or string or undefined, in case tuple is not valid
 */
function getWaypointTuple(
  partial: string,
  currentUserPosition?: Position | null
): WaypointTuple | undefined | string {
  if (partial === '') return undefined

  if (partial.startsWith('e-')) {
    const decodedPlace = JSON.parse(window.atob(partial.replace(/e-/, ''))) as DecodedPlace

    if (decodedPlace.latitude === undefined || decodedPlace.longitude === undefined) {
      console.warn('Warning: failed to get latitude or longitude for encoded value')

      return undefined
    }

    const name =
      decodedPlace.name !== undefined
        ? decodedPlace.name
        : decodedPlace.address !== undefined
        ? decodedPlace.address
        : ''

    return [name, decodedPlace.latitude, decodedPlace.longitude]
  }

  const rawValues = partial.split(',')

  if (rawValues.length > 0) {
    const name = decodeURIComponent(rawValues[0])

    if ((name === 'mylocation' || name === 'Your location') && rawValues.length < 3) {
      if (currentUserPosition) {
        return [name, currentUserPosition.lat, currentUserPosition.lng]
      }

      console.warn("Warning: missing user's current location")

      return 'Your location'
    }

    if (rawValues.length % 3 === 0) {
      const coordinates: [number, number] = [Number(rawValues[1]), Number(rawValues[2])]

      if (coordinates.find((coord) => isNaN(coord)) === undefined) {
        return [name, ...coordinates]
      }
    }
  }

  console.error('Error: failed to getWaypointPaths, coordinates are invalid', partial)

  return undefined
}

export const logRefFromURL = (url: string): void => {
  const parsedUrl = new URL(url)
  const searchParams = new URLSearchParams(parsedUrl.search)
  const ref = searchParams.get('ref')
  const linkType = getLinkType(parsedUrl.pathname)

  if (ref !== null && linkType !== null) {
    void logAppEvent(AppEvent.ExternalLinkOpened, {
      LinkType: linkType,
      Ref: ref,
    })
  }
}

const getLinkType = (pathName: string): string | null => {
  if (pathName.startsWith(`/${mapConfig.url.path.location}`)) {
    return 'location'
  }

  if (pathName.startsWith(`/${mapConfig.url.path.pdc}`)) {
    return 'place'
  }

  if (pathName.startsWith(`/${mapConfig.url.path.directions}`)) {
    return 'directions'
  }

  return null
}
